import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Send, Sparkles, User, Bot, Loader2, X, ChevronDown, Mic, MicOff } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

// Star component for constellation
const Star = ({ x, y, size = 4, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: [0.4, 1, 0.4], scale: 1 }}
    transition={{ 
      opacity: { duration: 2, repeat: Infinity, delay },
      scale: { duration: 0.3, delay }
    }}
    className="absolute rounded-full bg-[#F3EFE0]"
    style={{ 
      left: `${x}%`, 
      top: `${y}%`, 
      width: size, 
      height: size,
      boxShadow: '0 0 8px rgba(243, 239, 224, 0.8)'
    }}
  />
);

// Line connecting stars
const ConstellationLine = ({ x1, y1, x2, y2, delay = 0 }) => (
  <motion.line
    x1={`${x1}%`}
    y1={`${y1}%`}
    x2={`${x2}%`}
    y2={`${y2}%`}
    stroke="rgba(212, 175, 55, 0.3)"
    strokeWidth="1"
    initial={{ pathLength: 0, opacity: 0 }}
    animate={{ pathLength: 1, opacity: 1 }}
    transition={{ duration: 1.5, delay }}
  />
);

export const AstrologySearchBar = ({ profiles, selectedProfileId, onSelectProfile, isHomepage = false }) => {
  const { t, language } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        setSpeechSupported(true);
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'en-IN'; // Indian English for better Vedic terms

        recognitionRef.current.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map(result => result[0])
            .map(result => result.transcript)
            .join('');
          setInput(transcript);
        };

        recognitionRef.current.onend = () => {
          setIsListening(false);
        };

        recognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
        };
      }
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const toggleListening = () => {
    if (!speechSupported) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setInput('');
      setIsExpanded(true);
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const selectedProfile = profiles?.find(p => p.profile_id === selectedProfileId);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      type: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setIsExpanded(true);

    try {
      const response = await axios.post(
        `${API}/chat/ask`,
        {
          message: userMessage.content,
          profile_id: selectedProfileId || null
        },
        { withCredentials: true }
      );

      const botMessage = {
        type: 'bot',
        content: response.data.response,
        profileUsed: response.data.profile_used,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      let errorContent = "I couldn't process your question. Please try again.";
      
      // Check for usage limit error
      if (error.response?.status === 403) {
        errorContent = error.response.data.detail || "You've reached your free question limit. Subscribe to Stellar or Cosmic plan for unlimited access!";
      }
      
      const errorMessage = {
        type: 'bot',
        content: errorContent,
        isError: true,
        isLimitReached: error.response?.status === 403,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setIsExpanded(false);
  };

  const suggestedQuestions = [
    { text: t('chat.question1'), x: 12, y: 25 },
    { text: t('chat.question2'), x: 50, y: 15 },
    { text: t('chat.question3'), x: 88, y: 30 },
    { text: t('chat.question4'), x: 50, y: 75 }
  ];

  // Constellation points for the design
  const constellationStars = [
    { x: 20, y: 25, size: 5 },
    { x: 35, y: 15, size: 3 },
    { x: 50, y: 30, size: 6 },
    { x: 65, y: 20, size: 4 },
    { x: 80, y: 35, size: 5 },
    { x: 25, y: 55, size: 3 },
    { x: 45, y: 65, size: 4 },
    { x: 70, y: 60, size: 3 },
    { x: 55, y: 75, size: 5 },
  ];

  const constellationLines = [
    { x1: 20, y1: 25, x2: 35, y2: 15 },
    { x1: 35, y1: 15, x2: 50, y2: 30 },
    { x1: 50, y1: 30, x2: 65, y2: 20 },
    { x1: 65, y1: 20, x2: 80, y2: 35 },
    { x1: 50, y1: 30, x2: 45, y2: 65 },
    { x1: 25, y1: 55, x2: 45, y2: 65 },
    { x1: 45, y1: 65, x2: 70, y2: 60 },
    { x1: 70, y1: 60, x2: 55, y2: 75 },
  ];

  return (
    <div className={`w-full ${isHomepage ? 'max-w-4xl' : 'max-w-3xl'} mx-auto px-4`}>
      {/* Constellation Container */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(5,5,10,0.9) 0%, rgba(18,10,31,0.95) 100%)',
          border: '1px solid rgba(212, 175, 55, 0.15)'
        }}
      >
        {/* Constellation Background */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Stars */}
          {constellationStars.map((star, i) => (
            <Star key={i} x={star.x} y={star.y} size={star.size} delay={i * 0.1} />
          ))}
          
          {/* Connecting Lines */}
          <svg className="absolute inset-0 w-full h-full">
            {constellationLines.map((line, i) => (
              <ConstellationLine key={i} {...line} delay={i * 0.15} />
            ))}
          </svg>

          {/* Glow effect in center */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-[#C84B32] opacity-5 blur-3xl" />
        </div>

        <div className={`relative z-10 ${isHomepage ? 'p-8 sm:p-10' : 'p-6 sm:p-8'}`}>
          {/* Header */}
          <div className="text-center mb-6">
            <motion.div
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#C84B32]/10 border border-[#C84B32]/20 mb-4"
            >
              <Sparkles className="w-4 h-4 text-[#C84B32]" />
              <span className="text-[#C84B32] text-xs uppercase tracking-wider">Jyotish Guide</span>
            </motion.div>
            
            <h3 className={`font-['Fraunces'] ${isHomepage ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'} text-[#282622] mb-2`}>
              {isHomepage ? (
                <>💬 {t('chat.title')}</>
              ) : (
                <>Ask the <span className="gradient-text">Stars</span></>
              )}
            </h3>
            <p className="text-[#6B6560] text-xs sm:text-sm">
              {isHomepage ? t('chat.subtitle') : 'Your cosmic companion for Vedic wisdom'}
            </p>
          </div>

          {/* Profile Selector */}
          {profiles && profiles.length > 0 && (
            <div className="flex justify-center mb-5">
              <div className="relative">
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFF9EF] border border-[#282622]/10 text-sm hover:border-[#C84B32]/30 transition-colors"
                >
                  {selectedProfile ? (
                    <>
                      <div 
                        className="w-5 h-5 rounded-full flex items-center justify-center text-xs text-white"
                        style={{ background: selectedProfile.avatar_color }}
                      >
                        {selectedProfile.name[0]}
                      </div>
                      <span className="text-[#282622] text-xs sm:text-sm">{selectedProfile.name}</span>
                    </>
                  ) : (
                    <span className="text-[#6B6560] text-xs sm:text-sm">Select profile</span>
                  )}
                  <ChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 text-[#6B6560] transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {showProfileDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white border border-[#282622]/10 rounded-xl overflow-hidden z-20 shadow-xl"
                    >
                      <button
                        onClick={() => {
                          onSelectProfile(null);
                          setShowProfileDropdown(false);
                        }}
                        className={`w-full p-3 text-left text-sm hover:bg-[#FCE9D9]/60 transition-colors ${!selectedProfileId ? 'text-[#C84B32]' : 'text-[#6B6560]'}`}
                      >
                        General guidance
                      </button>
                      {profiles.map(profile => (
                        <button
                          key={profile.profile_id}
                          onClick={() => {
                            onSelectProfile(profile.profile_id);
                            setShowProfileDropdown(false);
                          }}
                          className={`w-full p-3 text-left text-sm hover:bg-[#FCE9D9]/60 transition-colors flex items-center gap-2 ${selectedProfileId === profile.profile_id ? 'text-[#C84B32]' : 'text-[#282622]'}`}
                        >
                          <div 
                            className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white"
                            style={{ background: profile.avatar_color }}
                          >
                            {profile.name[0]}
                          </div>
                          {profile.name}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="relative mb-5">
            <div className={`flex items-center gap-2 sm:gap-3 ${isHomepage ? 'px-4 sm:px-6 py-3 sm:py-5' : 'px-3 sm:px-5 py-2.5 sm:py-4'} rounded-2xl bg-[#FFF9EF] backdrop-blur border transition-all ${
              isExpanded ? 'border-[#C84B32]/50 shadow-lg shadow-[#C84B32]/5' : 'border-[#282622]/10 hover:border-[#282622]/15'
            } ${isListening ? 'border-red-500/50 shadow-red-500/10' : ''}`}>
              <Sparkles className={`w-4 h-4 sm:w-5 sm:h-5 text-[#C84B32] flex-shrink-0 ${isHomepage ? 'sm:w-6 sm:h-6' : ''}`} />
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => setIsExpanded(true)}
                placeholder={isListening ? t('chat.listening') : t('chat.placeholder')}
                className={`flex-1 min-w-0 bg-transparent text-[#282622] placeholder-[#A09DB0]/50 outline-none text-sm sm:text-base ${isHomepage ? 'sm:text-lg' : ''} ${isListening ? 'placeholder-red-400/70' : ''}`}
                data-testid="astrology-search-input"
              />
              
              {/* Voice Input Button - Hidden on very small screens if space is tight */}
              {speechSupported && (
                <button
                  onClick={toggleListening}
                  data-testid="voice-input-btn"
                  className={`p-2 sm:p-2.5 ${isHomepage ? 'sm:p-3' : ''} rounded-full transition-all flex-shrink-0 ${
                    isListening 
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                      : 'bg-white hover:bg-[#FCE9D9]/60 border border-[#282622]/10 hover:border-[#C84B32]/30'
                  }`}
                  title={isListening ? "Stop listening" : "Voice input"}
                >
                  {isListening ? (
                    <MicOff className="w-4 h-4 text-white" />
                  ) : (
                    <Mic className="w-4 h-4 text-[#C84B32]" />
                  )}
                </button>
              )}
              
              {input.trim() && (
                <button
                  onClick={handleSend}
                  disabled={isLoading}
                  className={`p-2 sm:p-2.5 ${isHomepage ? 'sm:p-3' : ''} rounded-full bg-gradient-to-r from-[#C84B32] to-[#F5B942] hover:from-[#E85D43] hover:to-[#F5B942] transition-all flex-shrink-0`}
                  data-testid="send-search-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 text-white" />
                  )}
                </button>
              )}
            </div>
            
            {/* Voice listening indicator */}
            {isListening && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 text-red-600 text-xs"
              >
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                {t('chat.listening')}
              </motion.div>
            )}
          </div>

          {/* Suggested Questions - Clickable Pills */}
          {!isExpanded && messages.length === 0 && (
            <div className={`${isHomepage ? 'pt-2 pb-4' : 'py-1'} ${isListening ? 'mt-4' : ''}`}>
              <p className="text-center text-[#8A8279] text-xs mb-2 sm:mb-3">{t('chat.clickToAsk')}</p>
              <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center items-center gap-1.5 sm:gap-2 px-1">
                {suggestedQuestions.map((q, i) => (
                  <motion.button
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 + 0.3 }}
                    onClick={() => {
                      setInput(q.text);
                      setIsExpanded(true);
                      inputRef.current?.focus();
                    }}
                    className="w-full sm:w-auto px-3 py-1.5 rounded-full bg-[#FFF9EF] border border-[#C84B32]/20 text-[#C84B32] text-xs hover:bg-[#C84B32]/10 hover:border-[#C84B32]/40 transition-all text-center"
                  >
                    <span className="mr-1">✦</span>
                    {q.text}
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          {/* Chat Messages */}
          <AnimatePresence>
            {isExpanded && messages.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 rounded-2xl bg-[#FFF9EF] border border-[#282622]/10 overflow-hidden max-h-[40vh] sm:max-h-[50vh]"
              >
                {/* Chat header */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#282622]/10 bg-[#FFF9EF]">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#C84B32] animate-pulse" />
                    <span className="text-[#6B6560] text-xs">Cosmic guidance active</span>
                  </div>
                  <button
                    onClick={clearChat}
                    className="p-1 rounded-lg hover:bg-[#FCE9D9] text-[#6B6560] hover:text-[#282622] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Messages */}
                <div className="p-4 space-y-4 overflow-y-auto max-h-[35vh]">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%]`}>
                        <div className={`flex items-start gap-2 ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                            msg.type === 'user' 
                              ? 'bg-[#C84B32]' 
                              : 'bg-gradient-to-br from-[#C84B32] to-[#F5B942]'
                          }`}>
                            {msg.type === 'user' ? (
                              <User className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                            ) : (
                              <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                            )}
                          </div>
                          <div className={`rounded-2xl px-3 sm:px-4 py-2 sm:py-3 ${
                            msg.type === 'user'
                              ? 'bg-[#C84B32]/30 text-[#282622]'
                              : msg.isError
                                ? 'bg-red-500/10 border border-red-500/20 text-red-700'
                                : 'bg-white text-[#282622]'
                          }`}>
                            <p className="text-xs sm:text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                            {msg.profileUsed && (
                              <p className="text-[10px] sm:text-xs text-[#C84B32] mt-2 opacity-80">
                                ✦ Based on {msg.profileUsed}'s chart
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center">
                          <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                        </div>
                        <div className="bg-white rounded-2xl px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="flex gap-1">
                              <span className="w-1.5 h-1.5 bg-[#C84B32] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <span className="w-1.5 h-1.5 bg-[#C84B32] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <span className="w-1.5 h-1.5 bg-[#C84B32] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </span>
                            <span className="text-xs text-[#6B6560]">Reading the stars...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default AstrologySearchBar;
